import { useEffect } from "react";
import { getAudio } from "@/audio";

export const BPM = 180;
export const maydayClock = { start: 0, kick: 0 };
const ROOTS = [55, 55, 65.41, 49];
const RIFF = [0, 0, 12, 0, 7, 0, 12, 10, 0, 0, 12, 0, 15, 12, 10, 7];

const curve = (amt) => Float32Array.from({ length: 1024 }, (_, i) => {
  const x = (i / 512) - 1;
  return Math.tanh(x * amt);
});

const noiseBuf = (ctx) => {
  const b = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
};

const kick = (ctx, dest, t) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.frequency.setValueAtTime(260, t);
  o.frequency.exponentialRampToValueAtTime(48, t + 0.09);
  g.gain.setValueAtTime(1, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
  o.connect(g).connect(dest);
  o.start(t);
  o.stop(t + 0.3);
};

const hat = (ctx, dest, buf, t, open) => {
  const s = ctx.createBufferSource();
  s.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = "highpass";
  f.frequency.value = 7000;
  const g = ctx.createGain();
  g.gain.setValueAtTime(open ? 0.25 : 0.15, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + (open ? 0.12 : 0.03));
  s.connect(f).connect(g).connect(dest);
  s.start(t);
  s.stop(t + 0.15);
};

const clap = (ctx, dest, buf, t) => {
  const s = ctx.createBufferSource();
  s.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = 1500;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.5, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
  s.connect(f).connect(g).connect(dest);
  s.start(t);
  s.stop(t + 0.2);
};

const hoover = (ctx, dest, f, t, dur) => {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.09, t + 0.01);
  g.gain.setTargetAtTime(0.0001, t + dur * 0.8, 0.02);
  g.connect(dest);
  [-12, 0, 7].forEach((det, i) => {
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(f * (i === 2 ? 2 : 1), t);
    o.detune.value = det;
    o.connect(g);
    o.start(t);
    o.stop(t + dur + 0.05);
  });
};

const startEngine = () => {
  const { ctx, out } = getAudio();
  const bus = ctx.createGain();
  bus.gain.value = 0.55;
  const drive = ctx.createWaveShaper();
  drive.curve = curve(6);
  const kickBus = ctx.createGain();
  kickBus.connect(drive).connect(bus);
  const synth = ctx.createBiquadFilter();
  synth.type = "lowpass";
  synth.Q.value = 8;
  synth.frequency.value = 2400;
  const synthDrive = ctx.createWaveShaper();
  synthDrive.curve = curve(3);
  synth.connect(synthDrive).connect(bus);
  bus.connect(out);
  const buf = noiseBuf(ctx);
  const step = 60 / BPM / 4;
  let n = 0;
  let next = ctx.currentTime + 0.08;
  maydayClock.start = performance.now() + 80;
  const tick = () => {
    while (next < ctx.currentTime + 0.12) {
      const s = n % 16, bar = Math.floor(n / 16);
      if (s % 4 === 0) {
        kick(ctx, kickBus, next);
        const at = (next - ctx.currentTime) * 1000;
        if (s % 8 === 0) setTimeout(() => { maydayClock.kick = performance.now(); }, at);
      }
      if (s % 4 === 2) hat(ctx, bus, buf, next, true);
      else if (s % 2 === 1) hat(ctx, bus, buf, next, false);
      if (s === 4 || s === 12) clap(ctx, bus, buf, next);
      if (bar % 8 !== 7 || s < 8) {
        const root = ROOTS[Math.floor(bar / 2) % ROOTS.length];
        if (RIFF[s] !== undefined && (s % 2 === 0 || Math.random() < 0.5)) hoover(ctx, synth, root * 2 ** (RIFF[s] / 12) * 2, next, step * 0.9);
      }
      synth.frequency.setValueAtTime(1200 + 2400 * ((bar % 4) / 3), next);
      next += step;
      n++;
    }
  };
  const id = setInterval(tick, 25);
  tick();
  window.__maydayOn = true;
  window.__maydayStarts = (window.__maydayStarts || 0) + 1;
  return () => {
    clearInterval(id);
    window.__maydayOn = false;
    bus.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
    setTimeout(() => bus.disconnect(), 400);
  };
};

export const useMayday = (on) => {
  useEffect(() => (on ? startEngine() : undefined), [on]);
};
